import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, IconButton, Stack, Tooltip, Box, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

const RolesTable = ({ rolesList, onEdit, onDelete, editicon, deleteicon }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [selectedRoleName, setSelectedRoleName] = useState('');
  const [selfOnly, setSelfonly] = useState(false);

  const permissionColors = {
    read: '#3498db',
    write: '#2ecc71',
    update: '#f39c12',
    delete: '#e74c3c',
  };

  const handleOpenDialog = (permissions, roleName, self_only) => {
    setSelectedPermissions(permissions || {});
    setSelfonly(self_only)
    setSelectedRoleName(roleName || '');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPermissions({});
    setSelectedRoleName('');
  };

  const renderPermissionsDialog = () => {
    const modules = [
      'employees',
      'bills',
      'masters',
      'leads',
      'clients',
      'services',
      'rolemanagement',
      'followup',
      'tasks'
    ];


    return (
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Open Sans', fontWeight: 600, fontSize: '16px', textAlign: 'center' }}>
          Permissions for {selectedRoleName}
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead sx={{ backgroundColor: '#f0f9ff' }}>
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Open Sans', fontWeight: 600, fontSize: '12px', backgroundColor: '#f0f9ff' }}>
                    Module Name
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Open Sans', fontWeight: 600, fontSize: '12px', backgroundColor: '#f0f9ff' }}>
                    Permissions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((module, index) => {
                  const permissions = selectedPermissions[module]
                    ? Object.entries(selectedPermissions[module])
                      .filter(([_, value]) => value)
                      .map(([key]) => key.replace(/^can_/, ''))
                    : [];

                  return (
                    <TableRow key={module}>
                      <TableCell sx={{ fontFamily: 'Open Sans', fontWeight: 500, textTransform: 'capitalize' }}>
                        {module}
                      </TableCell>
                      <TableCell>
                        {permissions.length ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {permissions.map((perm, idx) => (
                              <Chip
                                key={`${module}-${perm}-${idx}`}
                                label={perm}
                                size="small"
                                sx={{
                                  fontSize: '9px',
                                  fontFamily: 'Open Sans',
                                  whiteSpace: 'nowrap',
                                  width: '55px',
                                  border: `1px solid ${permissionColors[perm]}`,
                                  borderRadius: 1,
                                  boxShadow: 2,
                                  color: permissionColors[perm],
                                  backgroundColor: '#f0f9ff',
                                  textTransform: 'capitalize',
                                  fontWeight: 600
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography sx={{ fontFamily: 'Open Sans', fontSize: '12px', fontWeight: 500 }}>
                            No permissions
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Open Sans', fontWeight: 500 }}>
                    Self Only
                  </TableCell>
                  <TableCell>
                    {selfOnly === 1 ? (
                      <Typography sx={{ fontFamily: 'Open Sans', fontSize: '12px', fontWeight: 500 }}>
                        Enabled
                      </Typography>
                    ) : (
                      <Typography sx={{ fontFamily: 'Open Sans', fontSize: '12px', fontWeight: 500 }}>
                        Disabled
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    );
  };

  if (!rolesList.length) {
    return (
      <Typography sx={{ m: 3, fontFamily: 'Open Sans' }}>
        No roles available
      </Typography>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 3 }} >
        <Table sx={{ minWidth: 650 }} size="small" stickyHeader >
          <TableHead sx={{ backgroundColor: '#f0f9ff' }}>
            <TableRow sx={{ textWrap: 'nowrap' }}>
              <TableCell sx={{ fontFamily: 'Open Sans', fontWeight: 600, fontSize: '12px', backgroundColor: '#f0f9ff', textAlign: 'center' }}>S.NO</TableCell>
              <TableCell sx={{ fontFamily: 'Open Sans', fontWeight: 600, fontSize: '12px', backgroundColor: '#f0f9ff', textAlign: 'center' }}>Role Name</TableCell>
              <TableCell sx={{ fontFamily: 'Open Sans', fontWeight: 600, fontSize: '12px', backgroundColor: '#f0f9ff', textAlign: 'center' }}>Permissions</TableCell>
              {(editicon || deleteicon) && <TableCell sx={{ fontFamily: 'Open Sans', fontWeight: 600, fontSize: '12px', backgroundColor: '#f0f9ff', textAlign: 'center' }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rolesList.map((role, index) => (
              <TableRow key={role.role_name || index}>
                <TableCell sx={{ fontFamily: 'Open Sans', fontWeight: 500 }}>
                  {index + 1}
                </TableCell>
                <TableCell sx={{ fontFamily: 'Open Sans', fontWeight: 500, textTransform: 'capitalize' }}>
                  {role.role_name}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Tooltip title="View Permissions" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(role.permissions, role.role_name, role.self_only)}
                      sx={{ color: '#0b85c8' }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                {(editicon || deleteicon) && <TableCell align="center">
                  <Stack
                    direction="row"
                    spacing={0}
                    justifyContent="center"
                    alignItems="center"
                  >
                    {editicon && <Tooltip title="Edit" arrow>
                      <IconButton
                        size="small"
                        onClick={() => onEdit(index)}
                        sx={{ color: '#0b85c8' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>}
                    {deleteicon && <Tooltip title="Delete" arrow>
                      <IconButton
                        size="small"
                        onClick={() => onDelete(role.role_name)}
                        sx={{ color: '#e74c3c' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>}
                  </Stack>
                </TableCell>}

              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {renderPermissionsDialog()}
    </>
  );
};

export default RolesTable;